import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

interface ProfileUser {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  isActive: boolean
  roles: Array<{
    role: {
      id: string
      name: string
      description: string
    }
  }>
  staffOrganization?: {
    id: string
    name: string
  }
  clientOrganizations?: Array<{
    id: string
    name: string
  }>
}

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/api/users/me')
      if (response.data.success) {
        setProfile(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      toast.error('Failed to fetch profile')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="card">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="card">
          <div className="text-center py-12">
            <p className="text-gray-500">Failed to load profile</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Profile</h1>
          <p className="mt-2 text-sm text-gray-700">
            View your account information
          </p>
        </div>
      </div>

      <div className="mt-8 card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
        
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">First Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{profile.firstName}</dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Last Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{profile.lastName}</dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">{profile.email}</dd>
          </div>

          {profile.phone && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone</dt>
              <dd className="mt-1 text-sm text-gray-900">{profile.phone}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="mt-8 card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Roles & Permissions</h2>
        
        <div className="space-y-4">
          {profile.roles.map((userRole) => (
            <div key={userRole.role.id} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{userRole.role.name}</p>
                {userRole.role.description && (
                  <p className="text-sm text-gray-500">{userRole.role.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {profile.staffOrganization && (
        <div className="mt-8 card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Staff Organization</h2>
          
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Organization</dt>
              <dd className="mt-1 text-sm text-gray-900">{profile.staffOrganization.name}</dd>
            </div>
          </dl>
        </div>
      )}

      {profile.clientOrganizations && profile.clientOrganizations.length > 0 && (
        <div className="mt-8 card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Client Organizations</h2>
          
          <div className="space-y-2">
            {profile.clientOrganizations.map((org) => (
              <div key={org.id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                <p className="text-sm text-gray-900">{org.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Status</h2>
        
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                profile.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {profile.isActive ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export default Profile
